import { QueryClient } from '@tanstack/react-query';

import { circuitBreakerAction } from '@telialt/utils/src/CircuitBreaker/circuitBreakerAction';
import { TeliaSegment } from '@telialt/utils/src/segment';

import { apiGetFallbackSearchProductsQuery, apiGetSearchQuery } from '../../api';
import {
    CATEGORY_PRODUCTS_CIRCUIT_BREAKER_ERROR_THRESHOLD_PERCENTAGE,
    CATEGORY_PRODUCTS_CIRCUIT_BREAKER_REQUEST_TIMEOUT,
    CATEGORY_PRODUCTS_CIRCUIT_BREAKER_RESET_TIMEOUT,
    SEARCH_QUERY_KEYS,
} from '../../constants';
import { ProductSearchItem, SearchFilters, SearchQueryKeysMap, SearchQueryParams } from '../../model';

type Params = {
    segment: TeliaSegment;
    queryKeys: SearchQueryKeysMap;
    filters?: SearchFilters;
    sort?: Array<Record<string, 'asc' | 'desc'>>;
    offset?: number;
    limit?: number;
    signal?: AbortSignal;
    clientIp?: string;
};

export const loadCategoryProductsResults = async ({
    segment,
    queryKeys,
    filters,
    sort,
    offset,
    limit,
    signal,
    clientIp,
}: Params) => {
    const queryKeyPrefix = segment;

    const searchParams: SearchQueryParams = {
        searchText: '',
        filters,
        sort,
        offset,
        limit,
    };

    const response = await apiGetSearchQuery<ProductSearchItem>(
        queryKeys[`${queryKeyPrefix}-products-category`],
        searchParams,
        signal,
        clientIp,
    );

    return response.data;
};

export const loadFallbackCategoryProductsResults = async ({ filters, offset, signal }: Params) => {
    const categoryPath = filters.categoryPath?.[0];

    const response = await apiGetFallbackSearchProductsQuery(categoryPath, offset, signal);

    return response.data;
};

export const loadCategoryProductsCircuitBreaker = circuitBreakerAction({
    mainAction: loadCategoryProductsResults,
    fallbackAction: loadFallbackCategoryProductsResults,
    options: {
        timeout: CATEGORY_PRODUCTS_CIRCUIT_BREAKER_REQUEST_TIMEOUT,
        errorThresholdPercentage: CATEGORY_PRODUCTS_CIRCUIT_BREAKER_ERROR_THRESHOLD_PERCENTAGE,
        resetTimeout: CATEGORY_PRODUCTS_CIRCUIT_BREAKER_RESET_TIMEOUT,
    },
});

export const prefetchCategoryProductsResults = (queryClient: QueryClient, params: Params) => {
    const { segment, limit, offset, filters, sort } = params;

    return queryClient.fetchQuery({
        queryKey: [SEARCH_QUERY_KEYS.SEARCH_RESULTS_CATEGORY_PRODUCTS, segment, limit, offset, filters, sort],
        queryFn: () => loadCategoryProductsCircuitBreaker.fire(params),
    });
};
